import './globe-gallery.scss';
// Hier en niet in globe-scroll.js: de bol meet z'n straal bij init, dus de maten
// van de scroll-layout moeten er dan al zijn.
import './globe-scroll.scss';
import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin( Observer, ScrollTrigger );

// Osmo — 3D sphere carousel, 1-op-1 overgenomen van osmo.supply/product/
// button-pack (init3dSphereCarousel). Toevoegingen: prefers-reduced-motion zet de
// draaiing stil (bol blijft staan, diepte-fade blijft kloppen) en de straal volgt
// de lijstbreedte via een ResizeObserver.
// Draaisnelheid: duration 30 (sec per rondje); scroll-boost: velocityY * 0.005,
// geclampt 1–60× en in 1.2s terug naar 1×.
function init3dSphereCarousel({ container } = {}) {
  let hRadius;
  let vRadius;
  let observerInstance;
  let spin;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const root = container || document;
  const wrap = root.querySelector("[data-3d-carousel-wrap]");
  if (!wrap) return () => {};

  const list = wrap;
  const vAngles = [-50, -25, 0, 25, 50];

  const calcRadius = () => {
    const size = list.offsetWidth;
    const base = size * 0.6;

    hRadius = base;
    vRadius = base * 0.85;
  };

  const destroyGsapScene = () => {
    if (observerInstance) {
      observerInstance.kill();
      observerInstance = null;
    }

    if (spin) {
      spin.kill();
      spin = null;
    }

    const panels = wrap.querySelectorAll("[data-3d-carousel-panel]");
    gsap.set(panels, { clearProps: "transform" });

    const items = wrap.querySelectorAll("[data-3d-carousel-item]");
    gsap.set(items, { clearProps: "transform" });
  };

  const create = () => {
    calcRadius();

    const panels = wrap.querySelectorAll("[data-3d-carousel-panel]");
    panels.forEach((p) => {
      p.style.transformOrigin = `50% 50% ${-hRadius}px`;
    });

    const panelCache = [];

    panels.forEach((panel) => {
      const cached = [];

      panel.querySelectorAll("[data-3d-carousel-item]").forEach((item) => {
        const idx = parseInt(item.getAttribute("data-index"), 10);
        const vAngle = vAngles[idx];

        item.style.transformOrigin = `50% 50% ${-vRadius}px`;
        item.style.transform = `rotateX(${vAngle}deg)`;

        const content = item.querySelector("[data-3d-carousel-content]");
        if (content) {
          cached.push({
            content,
            vFactor: Math.cos((vAngle * Math.PI) / 180),
          });
        }
      });

      panelCache.push({ el: panel, items: cached });
    });

    const FADE_POWER = 1.6;

    function smoothstep(t) {
      t = Math.max(0, Math.min(1, t));
      return t * t * (3 - 2 * t);
    }

    function updateDepthFade() {
      for (let i = 0; i < panelCache.length; i++) {
        const { el, items } = panelCache[i];
        const raw = gsap.getProperty(el, "rotationY");
        const rad = (((raw % 360) + 360) % 360) * (Math.PI / 180);
        const panelCos = Math.cos(rad);

        for (let j = 0; j < items.length; j++) {
          const { content, vFactor } = items[j];
          const combined = Math.max(0, panelCos * vFactor);
          const curved = smoothstep(
            smoothstep(Math.pow(combined, 1 / FADE_POWER))
          );

          const opacity = 0.15 + 0.85 * curved;

          content.style.opacity = opacity;
          content.style.pointerEvents =
            opacity < 0.5 ? "none" : "auto";
        }
      }
    }

    spin = gsap.fromTo(
      panels,
      { rotationY: (i) => (i * 360) / panels.length },
      {
        rotationY: "-=360",
        duration: 30,
        ease: "none",
        repeat: -1,
        onUpdate: updateDepthFade,
      }
    );

    spin.progress(1000);
    if (reduceMotion) spin.pause();

    observerInstance = Observer.create({
      target: window,
      type: "wheel,scroll,touch",
      onChangeY: (self) => {
        const boost = Math.abs(self.velocityY * 0.005);
        const v = gsap.utils.clamp(1, 60, boost);
        spin.timeScale(v);

        gsap.to(spin, {
          timeScale: 1,
          duration: 1.2,
          ease: "power2.out",
        });
      },
    });
  };

  create();

  const modalEl = document.querySelector("[data-modal-target]");

  let modalObserver;

  if (modalEl) {
    modalObserver = new MutationObserver(() => {
      const isActive = modalEl.getAttribute("data-modal-status") === "active";

      if (isActive) {
        observerInstance?.disable();
      } else {
        observerInstance?.enable();
      }
    });

    modalObserver.observe(modalEl, {
      attributes: true,
      attributeFilter: ["data-modal-status"],
    });
  }

  function setPaused(paused) {
    if (!spin) return;

    spin.paused(paused || reduceMotion);

    if (observerInstance) {
      paused ? observerInstance.disable() : observerInstance.enable();
    }

    if (!paused) spin.timeScale(1);
  }

  const scrollHandler = ScrollTrigger.create({
    trigger: wrap,
    start: "top bottom",
    end: "bottom top",
    onEnter: () => setPaused(false),
    onEnterBack: () => setPaused(false),
    onLeave: () => setPaused(true),
    onLeaveBack: () => setPaused(true),
    invalidateOnRefresh: true,
  });

  const debounce = (fn, ms) => {
    let t;
    return () => {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  };

  const rebuild = debounce(() => {
    destroyGsapScene();
    create();
  }, 100);

  // Hellema: op de breedte van de lijst letten i.p.v. window resize. De maten
  // hangen aan de root font-size, die bij init nog niet altijd staat; dan klopt
  // de straal pas na de eerste resize.
  let measuredWidth = list.offsetWidth;
  const sizeObserver = new ResizeObserver(() => {
    if (list.offsetWidth === measuredWidth) return;
    measuredWidth = list.offsetWidth;
    rebuild();
  });
  sizeObserver.observe(list);

  return function destroyCarousel() {
    sizeObserver.disconnect();
    if (scrollHandler) scrollHandler.kill();
    if (modalObserver) modalObserver.disconnect();
    destroyGsapScene();
  };
}

// Eén instance per bol op de pagina (Osmo's init pakt de eerste wrap binnen
// `container`, dus we geven elke wrap z'n eigen container mee).
function initGlobeGalleries() {
  document.querySelectorAll(".sphere-carousel__wrap").forEach((container) => {
    init3dSphereCarousel({ container });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initGlobeGalleries);
} else {
  initGlobeGalleries();
}
